/**
 * Collection Set context and hooks
 *
 * Client-side state management for tagging endpoints for collection.
 * Unlike Map Set (which persists to backend), Collection Set is purely
 * client-side and per-case - it's a UI convenience for building a
 * collection request from selected endpoints.
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import type { CollectionSetContextValue } from '../types/collection-set'

const CollectionSetContext = createContext<CollectionSetContextValue | null>(null)

interface CollectionSetProviderProps {
  children: ReactNode
}

/**
 * Provider for Collection Set state
 *
 * Should be placed within the case context so it resets when changing cases.
 */
export function CollectionSetProvider({
  children,
}: CollectionSetProviderProps): React.ReactElement {
  const [taggedEndpointIds, setTaggedEndpointIds] = useState<Set<string>>(new Set())

  const addToSet = useCallback((endpointId: string) => {
    setTaggedEndpointIds((prev) => {
      if (prev.has(endpointId)) return prev
      const next = new Set(prev)
      next.add(endpointId)
      return next
    })
  }, [])

  const removeFromSet = useCallback((endpointId: string) => {
    setTaggedEndpointIds((prev) => {
      if (!prev.has(endpointId)) return prev
      const next = new Set(prev)
      next.delete(endpointId)
      return next
    })
  }, [])

  const addMultiple = useCallback((endpointIds: string[]) => {
    setTaggedEndpointIds((prev) => {
      const next = new Set(prev)
      for (const id of endpointIds) {
        next.add(id)
      }
      return next
    })
  }, [])

  const clearSet = useCallback(() => {
    setTaggedEndpointIds(new Set())
  }, [])

  const isInSet = useCallback(
    (endpointId: string) => taggedEndpointIds.has(endpointId),
    [taggedEndpointIds]
  )

  const count = useMemo(() => taggedEndpointIds.size, [taggedEndpointIds])

  const value = useMemo<CollectionSetContextValue>(
    () => ({
      taggedEndpointIds,
      addToSet,
      removeFromSet,
      addMultiple,
      clearSet,
      isInSet,
      count,
    }),
    [taggedEndpointIds, addToSet, removeFromSet, addMultiple, clearSet, isInSet, count]
  )

  return <CollectionSetContext.Provider value={value}>{children}</CollectionSetContext.Provider>
}

/**
 * Hook to access Collection Set context
 *
 * @throws Error if used outside of CollectionSetProvider
 */
export function useCollectionSet(): CollectionSetContextValue {
  const context = useContext(CollectionSetContext)
  if (!context) {
    throw new Error('useCollectionSet must be used within a CollectionSetProvider')
  }
  return context
}

/**
 * Hook to optionally access Collection Set context
 *
 * Returns null if used outside of CollectionSetProvider.
 * Useful for components that may or may not be within the provider.
 */
export function useCollectionSetOptional(): CollectionSetContextValue | null {
  return useContext(CollectionSetContext)
}
